import React, { useState } from 'react';
import { Info } from 'lucide-react';
import { supabase } from '../lib/supabase';

interface WishFormProps {
  onClose: () => void;
  onWishCreated: () => void;
  dark?: boolean;
  modern?: boolean;
}

export default function WishForm({ onClose, onWishCreated, dark, modern }: WishFormProps) {
  const [content, setContent] = useState('');
  const [authorName, setAuthorName] = useState('');
  const [displayType, setDisplayType] = useState<'full' | 'anonymous' | 'censored'>('full');
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [notification, setNotification] = useState('');

  const checkDailyWishLimit = async () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const { data: visibleWishes, error: countError } = await supabase
      .from('wishes')
      .select('id', { count: 'exact' })
      .eq('status', 0)
      .gte('created_at', today.toISOString());

    if (countError) {
      console.error('Error checking bubble limit:', countError);
      return 0;
    }

    return visibleWishes?.length || 0;
  };

  const handleSubmit = async () => {
    try {
      if (isSubmitting) return;
      setIsSubmitting(true);
      setError('');
      setNotification('');

      if (!content.trim() || !authorName.trim()) {
        setError('Please fill in all fields');
        return;
      }

      const wishCount = await checkDailyWishLimit();
      const isLimited = wishCount >= 5;
      
      // Masking author_name sesuai display type
      let maskedAuthorName = authorName.trim();
      if (displayType === 'anonymous') {
        maskedAuthorName = 'anon';
      } else if (displayType === 'censored') {
        // Sensor: ambil huruf pertama, sisanya diganti *
        maskedAuthorName = authorName.trim().split(' ').map(n => n[0] + '*'.repeat(Math.max(n.length - 1, 0))).join(' ');
      }
      const wishData = {
        content: content.trim(),
        author_name: maskedAuthorName,
        display_name_type: displayType,
        status: isLimited ? -1 : 0,
        scheduled_visibility_date: isLimited ? 
          new Date(new Date().setHours(24, 0, 0, 0)).toISOString() : 
          null
      };

      const { error: insertError } = await supabase
        .from('wishes')
        .insert(wishData)
        .select()
        .single();

      if (insertError) throw insertError;

      if (isLimited) {
        setNotification("Only five bubbles can request per day. This bubble will appear tomorrow or after admin approval.");
      } else {
        onWishCreated();
        onClose();
      }
    } catch (err) {
      setError('Failed to save bubble. Please try again.');
      console.error('Error saving bubble:', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-[#181825]/80 dark:bg-[#181825]/90 flex items-center justify-center z-50">
      <div className={`relative w-full max-w-md rounded-2xl p-0 shadow-2xl border border-[#b4befe]/30 overflow-hidden ${dark ? 'bg-[#232634]' : 'bg-[#f6faff]'} ${modern ? 'modern-surface' : ''}`}> 
        {/* Header with icon and title */}
        <div className="flex items-center gap-3 px-8 pt-8 pb-2">
          <div className="bg-[#b4befe] dark:bg-[#a6e3a1] rounded-full p-2 flex items-center justify-center">
            <span className="text-2xl">😛</span>
          </div>
          <div>
            <h3 className={`text-2xl font-extrabold ${dark ? 'text-[#f5e0dc]' : 'text-[#232634]'}`}>Yapping bubble</h3>
            <p className={`text-base mt-1 ${dark ? 'text-[#a6adc8]' : 'text-[#6c6f85]'}`}>Sometimes you just gotta yapp — entah itu rants, weird wishes, atau hal yang cuma kamu ngerti.</p>
          </div>
          <button
            onClick={onClose}
            className="absolute top-4 right-4 text-xl text-[#a6adc8] hover:text-[#f38ba8] transition-colors"
            title="Close"
          >
            ✕
          </button>
        </div>
        {/* Form */}
        <form className="px-8 pt-2 pb-8 space-y-6" onSubmit={e => { e.preventDefault(); handleSubmit(); }}>
          {/* Name input */}
          <div>
            <label className={`block font-semibold mb-1 ${dark ? 'text-[#f5e0dc]' : 'text-[#232634]'}`}>Your name</label>
            <input
              type="text"
              placeholder="Your name"
              value={authorName}
              onChange={e => setAuthorName(e.target.value)}
              className={`w-full rounded-xl px-4 py-3 bg-[#232634]/80 dark:bg-[#232634] text-base outline-none border-none shadow-inner placeholder:text-[#a6adc8] ${dark ? 'text-white' : 'text-[#f5e0dc]'}`}
              autoFocus
            />
          </div>
          {/* Display type radio */}
          <div className="flex gap-6 items-center mb-2">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                checked={displayType === 'full'}
                onChange={() => setDisplayType('full')}
                className="accent-[#b4befe]"
              />
              <span className={`text-base ${dark ? 'text-[#f5e0dc]' : 'text-[#232634]'}`}>Full Name</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                checked={displayType === 'anonymous'}
                onChange={() => setDisplayType('anonymous')}
                className="accent-[#b4befe]"
              />
              <span className={`text-base ${dark ? 'text-[#f5e0dc]' : 'text-[#232634]'}`}>Anonymous</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                checked={displayType === 'censored'}
                onChange={() => setDisplayType('censored')}
                className="accent-[#b4befe]"
              />
              <span className={`text-base ${dark ? 'text-[#f5e0dc]' : 'text-[#232634]'}`}>Censored</span>
            </label>
          </div>
          {/* Wish textarea */}
          <div>
            <textarea
              placeholder="Go yappin about anything you want!"
              value={content}
              onChange={e => setContent(e.target.value)}
              className={`w-full h-28 rounded-xl px-4 py-3 bg-[#232634]/80 dark:bg-[#232634] text-base outline-none border-none shadow-inner resize-none placeholder:text-[#a6adc8] ${dark ? 'text-white' : 'text-[#f5e0dc]'}`}
              maxLength={300}
            />
          </div>
          {/* Error/notification */}
          {error && (
            <p className="text-red-500 text-sm">{error}</p>
          )}
          {notification && (
            <div className="bg-gradient-to-r from-blue-500/20 to-purple-500/20 border border-blue-500/50 rounded-lg p-4 animate-fadeIn">
              <div className="flex items-start gap-3">
                <Info className="text-blue-400 shrink-0 mt-1" size={20} />
                <p className="text-[#a6adc8] text-sm">{notification}</p>
              </div>
            </div>
          )}
          {/* Submit button */}
          <button
            type="submit"
            disabled={isSubmitting}
            className={`w-full mt-2 bg-gradient-to-r from-[#b4befe] to-[#89b4fa] hover:from-[#a6e3a1] hover:to-[#b4befe] text-[#232634] dark:text-[#232634] font-bold py-3 rounded-xl flex items-center justify-center gap-2 text-lg shadow-lg transition-all disabled:opacity-60 disabled:cursor-not-allowed ${modern ? 'modern-btn' : ''}`}
          >
            <span className="text-xl">💬</span>
            {isSubmitting ? 'Saving...' : 'Yapp away'}
          </button>
        </form>
      </div>
    </div>
  );
}